import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import { Formik, Form, Field } from "formik";
import {
  FormGroup,
  FormControl,
  Button,
  Spinner,
  Toast,
} from "react-bootstrap";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("*Invalid email").required("*Email Required"),
  password: Yup.string()
    .min(8, "*Password must have at least 8 characters")
    .max(50, "*Password can't be longer than 50 characters")
    .required("*Password is required"),
});
const validAuthStates = ["signIn", "signedOut", "signedUp"];
const ERROR_USER_DISABLED = "User is disabled.";

const getUrlParams = () => {
  // Get query of url
  const query = encodeURI(window.location.href).split("?")[1];
  const results = [];
  if (query) {
    query.split("&").forEach((item) => {
      results.push(decodeURIComponent(item.split("=")[1]));
    });
  }

  return results;
};

const CustomSignIn = ({ authState, onStateChange }) => {
  const [showToast, setShowToast] = useState(false);
  const [signInStatus, setSignInStatus] = useState({
    isProcessing: false,
    errorMsg: "",
  });
  const [user, setUser] = useState({
    email: "",
    password: "",
  });

  useEffect(() => {
    const params = getUrlParams() || [];

    if (params.length) {
      setUser({
        email: params[0],
        password: params[1],
      });
    }
  }, []);

  useEffect(() => {
    if (user.email) {
      // Hide query in url after fill user data
      window.history.pushState(null, "", "/");
    }
  }, [user.email]);

  useEffect(() => {
    setSignInStatus({ isProcessing: false, errorMsg: "" });
  }, [authState]);

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  const signInSubmit = async (values) => {
    const { email, password } = values;

    try {
      setSignInStatus({ errorMsg: "", isProcessing: true });
      const res = await Auth.signIn(email, password);

      /**
       * If current account need to change password, change to newPassword
       * required form with current email and temp-password
       */
      if (res?.challengeName === "NEW_PASSWORD_REQUIRED") {
        onStateChange("newPasswordRequired", { email, password });
        return;
      }

      // Set the authState to "signedIn"
      onStateChange(authState);
    } catch (error) {
      const { code, message } = error;

      setSignInStatus({
        errorMsg: message,
        isProcessing: false,
      });

      /**
       * If user's account was not confirmed yet
       * Go to account-confirm form
       */
      if (code === "UserNotConfirmedException") {
        toggleToast();
        onStateChange("accountConfirm", { email });
      }
    }
  };

  const changeToForgotPasswordForm = () => {
    onStateChange("forgotPassword");
  };

  return (
    <>
      <Toast
        show={showToast}
        className="warning"
        onClose={toggleToast}
        autohide
      >
        <Toast.Body>Please verify your account!</Toast.Body>
      </Toast>
      {validAuthStates.includes(authState) ? (
        <Formik
          initialValues={{
            email: user.email,
            password: user.password,
          }}
          validationSchema={validationSchema}
          onSubmit={signInSubmit}
        >
          {({ errors, touched }) => (
            <Form className="auth-form">
              <Field name="email">
                {({ field }) => (
                  <FormGroup controlId="email">
                    <FormControl
                      type="email"
                      placeholder="Email"
                      onChange={field.onChange}
                      value={field.value}
                      data-prop="email"
                      className="auth-input"
                      isValid={touched.email && !errors.email}
                      isInvalid={touched.email && errors.email}
                      data-testid="email-signin"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.email}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="password">
                {({ field }) => (
                  <FormGroup controlId="password">
                    <FormControl
                      type="password"
                      placeholder="Password"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop="password"
                      className="auth-input"
                      isValid={touched.password && !errors.password}
                      isInvalid={touched.password && errors.password}
                      data-testid="password-signin"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.password}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <div>
                {signInStatus.errorMsg &&
                  (signInStatus.errorMsg === ERROR_USER_DISABLED ? (
                    <p className="error-message mb-1">
                      Account was disabled. Please contact our support team to{" "}
                      <strong>
                        <a href="mailto: support@bluecart.com">
                          support@bluecart.com
                        </a>
                      </strong>
                    </p>
                  ) : (
                    <p className="error-message mb-1">
                      {signInStatus.errorMsg}
                    </p>
                  ))}
                <Button type="submit" disabled={signInStatus.isProcessing}>
                  Sign in
                  {signInStatus.isProcessing && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="isProcessing"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  )}
                </Button>
                <Button
                  onClick={changeToForgotPasswordForm}
                  variant="link"
                  className="auth-navigate"
                  data-testid="forgot-password"
                >
                  <em>Forgot your password?</em>
                </Button>
                {/*<p>
                  {`Don't have an account?`}{" "}
                  <Button
                    variant="link"
                    onClick={() => onStateChange("signUp")}
                  >
                    <strong>Sign up</strong>
                  </Button>
                </p>*/}
              </div>
            </Form>
          )}
        </Formik>
      ) : null}
    </>
  );
};

CustomSignIn.propTypes = {
  authState: PropTypes.string,
  onStateChange: PropTypes.func,
};

export default CustomSignIn;
